import React, { useState } from "react";
import { UserProfile } from "../types/objects";
import { STRAPI_URL } from "../api/strapi";

/**
 * Props interface for LoginForm component
 */
interface LoginFormProps {
	onLogin: (user: UserProfile, token: string) => void;
}

/**
 * LoginForm Component
 * Handles multi-step authentication for Strapi 5 to ensure role data is populated.
 */
export default function LoginForm({ onLogin }: LoginFormProps) {
	const [identifier, setIdentifier] = useState<string>("");
	const [password, setPassword] = useState<string>("");
	const [error, setError] = useState<string>("");
	const [isLoading, setIsLoading] = useState<boolean>(false);

	/**
	 * Form Submission Handler
	 * Step 1: Authenticate and receive JWT.
	 * Step 2: Fetch full profile with role using /users/me?populate=role.
	 */
	const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setError("");
		setIsLoading(true);

		try {
			// Step 1: Authentication request (no populate support here in Strapi 5)
			const authRes = await fetch(`${STRAPI_URL}/api/auth/local`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ identifier, password }),
			});

			const authData = await authRes.json();

			if (!authRes.ok) {
				throw new Error(authData.error?.message || "Invalid credentials");
			}

			if (authData.jwt) {
				// Step 2: Fetch enriched user data including role via authenticated request
				const userRes = await fetch(`${STRAPI_URL}/api/users/me?populate=role`, {
					headers: { Authorization: `Bearer ${authData.jwt}` }
				});

				const userData = await userRes.json();

				if (!userRes.ok) {
					throw new Error("Failed to retrieve populated user profile");
				}

				// Finalize the login process with enriched user object
				localStorage.setItem("token", authData.jwt);
				onLogin(userData as UserProfile, authData.jwt);
			}
		} catch (err: any) {
			console.error("Login failed:", err.message);
			// fetch() rejects with TypeError when the server is unreachable
			setError(err instanceof TypeError ? "Server je nedostupný, zkuste to později" : "Neplatné jméno nebo heslo");
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="login-wrapper">
			<form onSubmit={handleLogin} className="login-form-dynamic">
				<input
					type="text"
					placeholder="Jméno"
					value={identifier}
					onChange={(e) => setIdentifier(e.target.value)}
					className="login-input"
					disabled={isLoading}
					autoComplete="username"
					required
				/>

				<input
					type="password"
					placeholder="Heslo"
					value={password}
					onChange={(e) => setPassword(e.target.value)}
					className="login-input"
					disabled={isLoading}
					autoComplete="current-password"
					required
				/>

				<button
					type="submit"
					className="login-submit-btn"
					disabled={isLoading}
				>
					{isLoading ? "..." : "Přihlásit"}
				</button>

				{error && <span className="login-error-msg">{error}</span>}
			</form>

			<style>{`
				/* Mobile Layout (Default - vertical stack) */
				.login-form-dynamic {
					display: flex;
					flex-direction: column;
					gap: 10px;
					width: 100%;
				}

				.login-input {
					padding: 12px 16px;
					border-radius: 10px;
					border: 1px solid #cbd5e1;
					font-size: 16px;
					width: 100%;
					box-sizing: border-box;
				}

				.login-submit-btn {
					padding: 12px;
					border-radius: 10px;
					background-color: #2563eb;
					color: white;
					border: none;
					font-weight: 800;
					cursor: pointer;
				}

				.login-error-msg {
					color: #ef4444;
					font-size: 11px;
					font-weight: 800;
					text-align: center;
				}

				/* Desktop Layout (Navbar inline row) */
				@media (min-width: 1251px) {
					.login-form-dynamic {
						flex-direction: row;
						align-items: center;
						gap: 6px;
					}

					.login-input {
						padding: 8px 12px;
						width: 130px;
						font-size: 13px;
					}

					.login-submit-btn {
						padding: 8px 15px;
						font-size: 13px;
					}

					.login-error-msg {
						position: absolute;
						bottom: -18px;
						right: 0;
					}

					.login-wrapper {
						position: relative;
					}
				}
			`}</style>
		</div>
	);
}
